import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { FormattedMessage, defineMessages } from 'react-intl';

const messages = defineMessages({
    placeholder: {
        id: 'invoices.detail.placeholder',
        defaultMessage: 'Select an invoice on the left to see details'
    },
    clearSelection: {
        id: 'invoices.detail.clearSelection',
        defaultMessage: 'Clear selection'
    },
    title: {
        id: 'invoices.detail.title',
        defaultMessage: 'Invoice detail'
    },
    dueDate: {
        id: 'invoices.detail.dueDate',
        defaultMessage: 'Due date'
    },
    amountToPay: {
        id: 'invoices.detail.amountToPay',
        defaultMessage: 'Amount to pay'
    },
    variableSymbol: {
        id: 'invoices.detail.variableSymbol',
        defaultMessage: 'Variable symbol'
    },
    logs: {
        id: 'invoices.detail.logs',
        defaultMessage: 'Logs'
    },
    communications: {
        id: 'invoices.detail.communications',
        defaultMessage: 'Communications'
    },
    logTime: {
        id: 'invoices.detail.logs.time',
        defaultMessage: 'Time'
    },
    logType: {
        id: 'invoices.detail.logs.type',
        defaultMessage: 'Type'
    },
    logTitle: {
        id: 'invoices.detail.logs.title',
        defaultMessage: 'Title'
    },
    logMessage: {
        id: 'invoices.detail.logs.message',
        defaultMessage: 'Message'
    },
    commTime: {
        id: 'invoices.detail.comms.time',
        defaultMessage: 'Time'
    },
    commType: {
        id: 'invoices.detail.comms.type',
        defaultMessage: 'Type'
    },
    commRecipient: {
        id: 'invoices.detail.comms.recipient',
        defaultMessage: 'Recipient'
    },
    commMessage: {
        id: 'invoices.detail.comms.message',
        defaultMessage: 'Message'
    }
});

export default class OverdueBillDetail extends React.Component {
    static propTypes = {
        bill: PropTypes.object,
        canSeeCommunications: PropTypes.bool,
        isDeveloper: PropTypes.bool,
        onClear: PropTypes.func,
        onNext: PropTypes.func,
        onPrev: PropTypes.func,
    };
    render() {
        const { bill } = this.props; // {id, code}
        if (!bill) return (
            <div className="panel panel-default">
                <div className="panel-heading">
                    <h3 className="panel-title"><FormattedMessage {...messages.title} /></h3>
                </div>
                <div className="panel-body text-muted" style={{ textAlign: 'center', padding: 16 }}>
                    <i className="fa fa-hand-o-left" style={{ marginRight: 6 }}></i>
                    <FormattedMessage {...messages.placeholder} />
                </div>
            </div>
        );
        const logs = bill.logs || [];
        const comms = bill.communications || [];
        const toText = (v) => {
            if (v == null) return '';
            if (typeof v === 'object') return JSON.stringify(v);
            return String(v);
        };
        const formatCzechNumber = (value) => {
            if (value == null || value === '') return '';
            const num = Number(value);
            if (Number.isNaN(num)) return String(value);
            // Format to 2 decimal places
            const formatted = num.toFixed(2);
            const [integerPart, decimalPart] = formatted.split('.');
            // Add non-breaking spaces as thousands separator
            const withSpaces = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00A0');
            // Use comma as decimal separator
            return `${withSpaces},${decimalPart}`;
        };
        const formatCurrency = (code) => {
            if (!code) return '';
            // Remove "code:" prefix from FlexiBee format
            return String(code).replace(/^code:/, '');
        };

        // Helper to render message with clickable Markdown links [text](url)
        const renderMessageWithLinks = (text) => {
            if (!text) return '';
            const parts = [];
            let lastIndex = 0;
            // Regex to find Markdown links: [text](url)
            const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
            let match;
            let key = 0;

            while ((match = linkRegex.exec(text)) !== null) {
                // Add text before the link
                if (match.index > lastIndex) {
                    parts.push(text.substring(lastIndex, match.index));
                }
                // Add the link
                const linkText = match[1];
                const linkUrl = match[2];
                parts.push(
                    <a href={linkUrl} key={`link-${key++}`} rel="noopener noreferrer" target="_blank">
                        {linkText}
                    </a>
                );
                lastIndex = match.index + match[0].length;
            }

            // Add remaining text
            if (lastIndex < text.length) {
                parts.push(text.substring(lastIndex));
            }

            return parts.length > 0 ? parts : text;
        };
        return (
            <div className="panel panel-default overdue-bill-detail" style={{ margin: 0, border: 'none', borderRadius: 0, height: '100%' }}>
                <div className="panel-heading clearfix" style={{ borderRadius: 0 }}>
                    <h3 className="panel-title" style={{ display: 'inline-block', marginTop: 5 }}>
                        <FormattedMessage {...messages.title} /> {toText(bill.code)}
                    </h3>
                    <div className="pull-right">
                        <div className="btn-group" style={{ marginRight: 10 }}>
                            <button className="btn btn-default btn-sm" onClick={() => this.props.onPrev && this.props.onPrev()} title="Previous invoice">
                                <i className="fa fa-chevron-left"></i>
                            </button>
                            <button className="btn btn-default btn-sm" onClick={() => this.props.onNext && this.props.onNext()} title="Next invoice">
                                <i className="fa fa-chevron-right"></i>
                            </button>
                        </div>
                        <button className="btn btn-default btn-sm" onClick={() => this.props.onClear && this.props.onClear()} title="Close">
                            <i className="fa fa-times"></i>
                        </button>
                    </div>
                </div>
                <div className="panel-body">
                    <div>
                        <strong><FormattedMessage {...messages.dueDate} />:</strong> {bill.dueDate ? moment(bill.dueDate).format('DD.MM.YYYY') : ''}
                    </div>
                    {bill.variableSymbol ? (
                        <div>
                            <strong><FormattedMessage {...messages.variableSymbol} />:</strong> {toText(bill.variableSymbol)}
                        </div>
                    ) : null}
                    <div>
                        <strong><FormattedMessage {...messages.amountToPay} />:</strong> {formatCzechNumber(bill.amountToPay)}{bill.currencyCode ? `\u00A0${formatCurrency(bill.currencyCode)}` : ''}
                    </div>
                    <hr/>
                    {this.props.isDeveloper ? (
                        <div style={{ marginBottom: 16 }}>
                            <h4><FormattedMessage {...messages.logs} /></h4>
                            <table className="table table-striped table-hover">
                                <thead>
                                    <tr>
                                        <th><FormattedMessage {...messages.logTime} /></th>
                                        <th><FormattedMessage {...messages.logType} /></th>
                                        <th><FormattedMessage {...messages.logTitle} /></th>
                                        <th><FormattedMessage {...messages.logMessage} /></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logs.map(l => (
                                        <tr key={l.id}>
                                            <td>{l.timestamp ? moment(l.timestamp).format('DD.MM.YYYY HH:mm') : ''}</td>
                                            <td>{toText(l.type)}</td>
                                            <td>{toText(l.title) || ''}</td>
                                            <td>{toText(l.message) || ''}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : null}
                    {this.props.canSeeCommunications ? (
                        <div>
                            <h4><FormattedMessage {...messages.communications} /></h4>
                            <table className="table table-striped table-hover">
                                <thead>
                                    <tr>
                                        <th><FormattedMessage {...messages.commTime} /></th>
                                        <th><FormattedMessage {...messages.commType} /></th>
                                        <th><FormattedMessage {...messages.commRecipient} /></th>
                                        <th><FormattedMessage {...messages.commMessage} /></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {comms.map(c => {
                                        // Use logMessage if available (contains reference date & days overdue), otherwise use communication message
                                        const rawMsg = toText(c.logMessage || c.message) || '';
                                        // Remove duplicate recipient info (To:/Cc:) from message to avoid duplication with Recipient column
                                        // Remove "To: ..." up to next marker (Cc:/Rule:) or end, then remove "Cc: ..." similarly
                                        let cleaned = rawMsg.replace(/\s*To:\s*.*?(?=(?:\s+Cc:|\s+Rule:|$))/g, '');
                                        cleaned = cleaned.replace(/\s*Cc:\s*.*?(?=(?:\s+Rule:|$))/g, '');
                                        // Normalize whitespace
                                        cleaned = cleaned.replace(/\s{2,}/g, ' ').trim();
                                        // Render message with clickable links
                                        const renderedMessage = renderMessageWithLinks(cleaned);
                                        return (
                                            <tr key={c.id}>
                                                <td>{c.timestamp ? moment(c.timestamp).format('DD.MM.YYYY HH:mm') : ''}</td>
                                                <td>{toText(c.type)}</td>
                                                <td>{toText(c.recipient) || ''}</td>
                                                <td style={{ whiteSpace: 'pre-wrap' }}>{renderedMessage}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : null}
                </div>
            </div>
        );
    }
}


